import ClearIcon from '@mui/icons-material/Clear';
import HideSourceIconBase from '@mui/icons-material/HideSource';
import { Autocomplete, Box, Chip, CircularProgress, styled, TextField } from '@mui/material';
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import type { ColumnFilter, FilterOption } from 'util/filtering/models';
import { StyledStack } from './TextCriteriaContent.styled';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import {
  searchFlexCodesDebounced,
  searchFlexGlCodesDebounced,
  selectFlexCodingSlice,
} from 'app/pages/store/flexCodingSlice';
import { selectAgaveSlice } from 'app/pages/store/agaveSlice';
import { AgaveProject, AgaveGlCode, FlexCode, FlexGlCode } from 'API';
import { useAgaveSearch } from 'app/hooks/useAgaveSearch';
import { defaultIdentifierOrder, useGetOptionLabel } from 'app/hooks/useGetOptionLabel';
import { hasErpCodingUI as paidolHasErpCodingUI } from 'app/util/paidolAccountType';

const HideSourceIcon = styled(HideSourceIconBase)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
}));

const CustomChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  maxWidth: '100%',
  '& .MuiChip-deleteIcon': {
    width: theme.spacing(2.25),
    height: theme.spacing(2.25),
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  '& .MuiChip-label': {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface SearchableTextCriteriaContentProps {
  selectedFilterOptions: FilterOption[];
  onSelectedFilterOptionsChange: (newOptions: FilterOption[], key: string) => void;
  filter: ColumnFilter;
}

export const SearchableTextCriteriaContent = ({
  selectedFilterOptions,
  onSelectedFilterOptionsChange,
  filter,
}: SearchableTextCriteriaContentProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');
  const dropdownId = useId();

  const { selectedPaidol, selectedPaidolId } = useAppSelector(selectUserCompanies);
  const hasErpCodingUI = paidolHasErpCodingUI(selectedPaidol);

  const searchType = filter.searchType as 'projects' | 'glCodes';

  // useGlobalState: true to read from preloaded data in retrieveUsersManagementFilterOptions
  const {
    searchResults: agaveSearchResults,
    isSearching: agaveIsSearching,
    isLoadingMore: agaveIsLoadingMore,
    hasMore: agaveHasMore,
    searchAgaveCode,
    loadMore: agaveLoadMore,
    clearSearch,
  } = useAgaveSearch<AgaveProject | AgaveGlCode>({
    dropdownId,
    useGlobalState: true,
    searchType,
  });

  const { searchProjects: flexProjects, searchGlCodes: flexGlCodes } = useAppSelector(selectFlexCodingSlice);
  const { agaveAccount } = useAppSelector(selectAgaveSlice);

  // SEE-1030: after an Agave->direct swap the live `agaveAccount` is gone, so
  // fall back to the paidol's persisted source-system slug, so a migrated QBO
  // paidol's search labels stay QBO-style (no "- (number)" suffix where the
  // number is just the source_id). Non-QBO ERPs are unaffected.
  const sourceSystemSlug =
    agaveAccount?.source_system?.slug ?? selectedPaidol?.agaveSourceSystemSlug ?? undefined;

  const isQBDClient = useMemo(() => sourceSystemSlug === 'quick-books-desktop', [sourceSystemSlug]);

  const isQBOClient = useMemo(() => sourceSystemSlug === 'quick-books-online', [sourceSystemSlug]);

  const getOptionLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: 'number',
    identifierOrder: defaultIdentifierOrder,
    nameField: 'name',
  });

  const isLoading = useMemo(() => {
    if (hasErpCodingUI) {
      return agaveIsSearching;
    }
    return searchType === 'projects' ? flexProjects?.loading : flexGlCodes?.loading;
  }, [hasErpCodingUI, agaveIsSearching, searchType, flexProjects?.loading, flexGlCodes?.loading]);

  // SEE-1392: only the flex path reports this; the Agave search has no equivalent yet.
  // Without it a failed search shows "No results found", which reads as "this paidol has
  // no matching projects" rather than "the search did not run".
  const flexSearchFailed = useMemo(() => {
    if (hasErpCodingUI) return false;
    return searchType === 'projects' ? !!flexProjects?.hadError : !!flexGlCodes?.hadError;
  }, [hasErpCodingUI, searchType, flexProjects?.hadError, flexGlCodes?.hadError]);

  // Use getOptionLabel to match formatting in AgaveProjectsDropdown and AgaveGlCodesDropdown
  const searchResults = useMemo((): FilterOption[] => {
    if (hasErpCodingUI) {
      if (searchType === 'projects') {
        return (agaveSearchResults as AgaveProject[]).map((p) => ({
          id: p.id,
          label: getOptionLabel(p) || `${p.number || 'Unknown'} - ${p.name || 'Unknown Project'}`,
        }));
      } else {
        return (agaveSearchResults as AgaveGlCode[]).map((g) => ({
          id: g.id,
          label: getOptionLabel(g) || `${g.number || 'Unknown'} - ${g.name || 'Unknown GL Code'}`,
        }));
      }
    } else {
      if (searchType === 'projects') {
        return (flexProjects?.output.items || []).map((p: FlexCode) => ({
          id: p.id,
          label: p.projectName + (p.projectCode ? ` (${p.projectCode})` : ''),
        }));
      } else {
        return (flexGlCodes?.output.items || []).map((g: FlexGlCode) => ({
          id: g.id,
          label: g.name || g.id,
        }));
      }
    }
  }, [hasErpCodingUI, searchType, agaveSearchResults, flexProjects, flexGlCodes, getOptionLabel]);

  const filteredOptions = useMemo(() => {
    return searchResults.filter((opt) => !selectedFilterOptions.some((c) => c.id === opt.id));
  }, [searchResults, selectedFilterOptions]);

  const loadFlexDataOnMount = useCallback(() => {
    if (!selectedPaidolId) return;

    if (searchType === 'projects') {
      dispatch(
        searchFlexCodesDebounced({
          paidolId: selectedPaidolId,
          glCodeId: '',
          searchTerm: '',
          isSettingsRequest: true,
        })
      );
    } else {
      dispatch(
        searchFlexGlCodesDebounced({
          paidolId: selectedPaidolId,
          searchTerm: '',
          isSettingsRequest: true,
        })
      );
    }
  }, [dispatch, selectedPaidolId, searchType]);

  // Agave: server-side search. Flex: client-side filtering (Autocomplete handles it)
  const handleSearch = useCallback(
    (term: string) => {
      if (hasErpCodingUI) {
        searchAgaveCode(term);
      }
    },
    [hasErpCodingUI, searchAgaveCode]
  );

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    if (reason === 'input') {
      setInputValue(value);
      handleSearch(value);
    } else if (reason === 'clear') {
      setInputValue('');
    }
  };

  const handleLoadMore = useCallback(() => {
    if (hasErpCodingUI && agaveHasMore && !agaveIsLoadingMore) {
      agaveLoadMore();
    }
  }, [hasErpCodingUI, agaveHasMore, agaveIsLoadingMore, agaveLoadMore]);

  const hasLoadedInitial = useRef(false);

  const handleOpen = useCallback(() => {
    if (hasErpCodingUI && !hasLoadedInitial.current) {
      hasLoadedInitial.current = true;
      searchAgaveCode('');
    } else if (!hasErpCodingUI && !hasLoadedInitial.current) {
      hasLoadedInitial.current = true;
      loadFlexDataOnMount();
    }
  }, [hasErpCodingUI, searchAgaveCode, loadFlexDataOnMount]);

  return (
    <>
      <StyledStack direction="row">
        {selectedFilterOptions.map((option, i) => (
          <CustomChip
            icon={<HideSourceIcon />}
            deleteIcon={<ClearIcon fontSize="small" color="info" />}
            key={i}
            color="primaryLighter"
            label={option.label}
            onDelete={() => {
              const newValues = [...selectedFilterOptions];
              newValues.splice(
                newValues.findIndex((val) => val.id === option.id),
                1
              );
              onSelectedFilterOptionsChange(newValues, filter.key);
            }}
          />
        ))}
      </StyledStack>
      <Autocomplete
        options={filteredOptions}
        value={null}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onOpen={handleOpen}
        onChange={(_, value, reason) => {
          if (reason === 'selectOption' && value) {
            onSelectedFilterOptionsChange([...selectedFilterOptions, value], filter.key);
            setInputValue('');
            clearSearch();
            hasLoadedInitial.current = false;
            inputRef.current?.blur();
          }
        }}
        getOptionLabel={(option) => option.label}
        loading={isLoading}
        filterOptions={hasErpCodingUI ? (x) => x : undefined}
        ListboxProps={{
          onScroll: (event: React.UIEvent<HTMLUListElement>) => {
            const listboxNode = event.currentTarget;
            if (
              agaveHasMore &&
              hasErpCodingUI &&
              listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50
            ) {
              handleLoadMore();
            }
          },
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <Box className="flex items-center gap-8 border border-solid border-grey-300 rounded-lg px-8">
            <HideSourceIcon color="primary" />
            <TextField
              {...params}
              variant="standard"
              placeholder={`Search ${filter.name.toLowerCase()}...`}
              inputRef={inputRef}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {isLoading || agaveIsLoadingMore ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          </Box>
        )}
        noOptionsText={
          isLoading
            ? t('tableDropdown.searching', 'Searching...')
            : flexSearchFailed
            ? t('flexCodingSearchError', "We couldn't load these options.")
            : t('advancedSearch.noResultsFound', 'No results found')
        }
      />
    </>
  );
};
